import { z } from 'zod';
import { dateTimeSchema, idSchema, nullableDateTimeSchema } from './common';
import { audioStatusSchema } from './enums';
import { runSummarySchema } from './generation';

/** SPEC §2/§7 — batches. */

/** Per-status asset counts used by filter chips and the export confirmation. */
export const statusCountsSchema = z.object({
  PENDING: z.number().int().min(0),
  GENERATING: z.number().int().min(0),
  FAILED: z.number().int().min(0),
  GENERATED: z.number().int().min(0),
  APPROVED: z.number().int().min(0),
  STALE: z.number().int().min(0),
});
export type StatusCounts = z.infer<typeof statusCountsSchema>;

export const batchSummarySchema = z.object({
  id: idSchema,
  name: z.string(),
  slug: z.string(),
  sourceFilename: z.string(),
  headers: z.array(z.string()),
  uploadedById: idSchema,
  uploadedByName: z.string().nullable(),
  createdAt: dateTimeSchema,
  deletedAt: nullableDateTimeSchema,
  rowCount: z.number().int().min(0),
  approvedRowCount: z.number().int().min(0),
  assetCounts: statusCountsSchema,
  activeRunId: idSchema.nullable(),
});
export type BatchSummary = z.infer<typeof batchSummarySchema>;

/** GET /api/batches */
export const listBatchesQuerySchema = z.object({
  includeDeleted: z.coerce.boolean().default(false),
  q: z.string().trim().max(200).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type ListBatchesQuery = z.infer<typeof listBatchesQuerySchema>;

export const listBatchesResponseSchema = z.object({
  batches: z.array(batchSummarySchema),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  hasMore: z.boolean(),
});
export type ListBatchesResponse = z.infer<typeof listBatchesResponseSchema>;

/**
 * GET /api/batches/:id — also carries the export confirmation data (SPEC §7):
 * "Export — 28 of 40 rows fully approved, 3 stale. Continue?"
 */
export const exportConfirmationSchema = z.object({
  totalRows: z.number().int().min(0),
  /** Rows whose EN and AR assets are both APPROVED and not stale. */
  fullyApprovedRows: z.number().int().min(0),
  /** Rows with at least one STALE asset. */
  staleRows: z.number().int().min(0),
  /** Rows with at least one asset that has never been generated. */
  missingRows: z.number().int().min(0),
  /** Rows with at least one FAILED asset. */
  failedRows: z.number().int().min(0),
  /** Rows whose arabic_text is blank (their AR cell stays empty on export). */
  blankArabicRows: z.number().int().min(0),
  /**
   * Rows holding at least one APPROVED asset published under a different
   * `publishing.deliveryFormat` than the one configured now — the export would mix
   * `.mp3` and `.wav` URLs until they are re-approved (SPEC §8).
   */
  deliveryFormatMismatchRows: z.number().int().min(0),
  suggestedFilename: z.string(),
});
export type ExportConfirmation = z.infer<typeof exportConfirmationSchema>;

/**
 * SPEC §7 — an approval that was interrupted between "objects written to GCS" and
 * "result committed to the database". The server re-drives or compensates these on
 * boot; anything still listed here needs a human to look at it.
 */
export const publishWarningSchema = z.object({
  rowId: idSchema,
  rowIndex: z.number().int().min(0),
  publishedSlug: z.string(),
  attemptCount: z.number().int().min(0),
  lastError: z.string().nullable(),
  createdAt: dateTimeSchema,
});
export type PublishWarning = z.infer<typeof publishWarningSchema>;

export const batchDetailSchema = batchSummarySchema.extend({
  statusCounts: statusCountsSchema,
  rowStatusCounts: z.object({
    total: z.number().int().min(0),
    approved: z.number().int().min(0),
    stale: z.number().int().min(0),
    failed: z.number().int().min(0),
    pending: z.number().int().min(0),
    generating: z.number().int().min(0),
    generated: z.number().int().min(0),
  }),
  exportConfirmation: exportConfirmationSchema,
  activeRun: runSummarySchema.nullable(),
  lastRun: runSummarySchema.nullable(),
  /** Interrupted publishes that boot repair could not settle (SPEC §7). */
  publishWarnings: z.array(publishWarningSchema),
});
export type BatchDetail = z.infer<typeof batchDetailSchema>;

export const batchDetailResponseSchema = z.object({ batch: batchDetailSchema });
export type BatchDetailResponse = z.infer<typeof batchDetailResponseSchema>;

/** PATCH /api/batches/:id — rename only (slug is frozen). */
export const updateBatchRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
});
export type UpdateBatchRequest = z.infer<typeof updateBatchRequestSchema>;

export const updateBatchResponseSchema = z.object({ batch: batchSummarySchema });
export type UpdateBatchResponse = z.infer<typeof updateBatchResponseSchema>;

/** DELETE /api/batches/:id — SUPER_ADMIN, soft delete only; never touches GCS. */
export const deleteBatchResponseSchema = z.object({
  batch: batchSummarySchema,
  deletedAt: dateTimeSchema,
});
export type DeleteBatchResponse = z.infer<typeof deleteBatchResponseSchema>;

/** Status filter chips with live counts (SPEC §11). */
export const filterCountsSchema = z.object({
  all: z.number().int().min(0),
  pending: z.number().int().min(0),
  generating: z.number().int().min(0),
  failed: z.number().int().min(0),
  generated: z.number().int().min(0),
  approved: z.number().int().min(0),
  stale: z.number().int().min(0),
});
export type FilterCounts = z.infer<typeof filterCountsSchema>;

export const rowFilterStatusSchema = z.union([z.literal('all'), audioStatusSchema]);
export type RowFilterStatus = z.infer<typeof rowFilterStatusSchema>;
